import { useEffect, useState } from 'react';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type { Me } from '../lib/api';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Admins and managers see the trial from this many days before it ends. */
export const TRIAL_BANNER_STAFF_DAYS = 14;
/** Every user sees the trial from this many days before it ends. */
export const TRIAL_BANNER_EVERYONE_DAYS = 7;

/**
 * Days left of the tenant's trial, counted like the subscription page (a part day is a day), when
 * the banner should show for this role; else null. The trial end comes from `/api/me`, which
 * already leaves it out unless a trial is actually in force.
 */
export function trialBannerDaysLeft(
  trialEndsAt: string | null | undefined,
  tenantRole: Me['tenantRole'],
  now: Date,
): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt);
  if (!Number.isFinite(end.getTime()) || end <= now) return null;

  const daysLeft = Math.ceil((end.getTime() - now.getTime()) / DAY_MS);
  const isStaff = tenantRole === 'TenantAdmin' || tenantRole === 'Manager';
  const window = isStaff ? TRIAL_BANNER_STAFF_DAYS : TRIAL_BANNER_EVERYONE_DAYS;
  return daysLeft <= window ? daysLeft : null;
}

const useStyles = makeStyles({
  root: {
    position: 'relative',
    zIndex: 40,
    // Leave room for the fixed menu trigger in the top-right corner.
    padding: `${tokens.spacingVerticalS} calc(${tokens.spacingHorizontalM} * 2 + 40px) ${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
});

type TrialBannerProps = {
  trialEndsAt: string | null | undefined;
  tenantRole: Me['tenantRole'];
  /** Opens the subscription page. Offered to tenant admins only — nobody else can buy. */
  onChoosePlan: () => void;
};

/**
 * App-wide notice that the tenant is on a trial: admins and managers see it for the last two weeks,
 * everyone for the last week, when it also turns from informational to a warning.
 */
export function TrialBanner({ trialEndsAt, tenantRole, onChoosePlan }: TrialBannerProps) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const now = useNow();

  const daysLeft = trialBannerDaysLeft(trialEndsAt, tenantRole, now);
  if (daysLeft === null) return null;

  const isTenantAdmin = tenantRole === 'TenantAdmin';
  const end = new Date(trialEndsAt!);
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(end);
  const endsToday = end.toDateString() === now.toDateString();

  return (
    <div className={styles.root}>
      <MessageBar intent={daysLeft <= TRIAL_BANNER_EVERYONE_DAYS ? 'warning' : 'info'} layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>
            {endsToday
              ? t('trialBanner.endsToday')
              : t('trialBanner.headline', { count: daysLeft })}
          </MessageBarTitle>
          {isTenantAdmin
            ? t('trialBanner.adminBody', { date })
            : t('trialBanner.userBody', { date })}
        </MessageBarBody>
        {isTenantAdmin && (
          <MessageBarActions>
            <Button appearance="primary" size="small" onClick={onChoosePlan}>
              {t('trialBanner.choosePlan')}
            </Button>
          </MessageBarActions>
        )}
      </MessageBar>
    </div>
  );
}

/** The current time, re-read every minute so the banner appears and counts down without a reload. */
function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}
